/**
 * Respuesta de la API para un histórico de apoyo o beca.
 *
 * <p>Representa los datos tal como los devuelve el backend, en formato
 * raw, antes de mapearlos al modelo de frontend.</p>
 */
export interface IHistoricoApoyoRes {
  numeroBeca?: string | null;
  numeroCVU?: string | null;
  fechaInicioBeca?: string | null; // ISO date string
  fechaFinBeca?: string | null; // ISO date string
  cveConvocatoria?: string | null;
  nombreConvocatoria?: string | null;
  tipoConvocatoria?: string | null;
  cveApoyoObtener?: string | null;
  descApoyoObtener?: string | null;
  cveEstatusBeca?: string | null;
  descEstatusBeca?: string | null;
  cvePais?: string | null;
  descPais?: string | null;
  razonBaja?: string | null;
  razonBajaDescripcion?: string | null;
  fechaUltimoMovimiento?: string | null; // ISO date string
  justificacion?: string | null;
  cvePrograma?: string | null;
  fuente?: string | null;
}

/**
 * Modelo de histórico de apoyo mapeado para el frontend.
 *
 * <p>Contiene únicamente los campos necesarios para la UI, mapeados
 * desde la respuesta de la API {@link IHistoricoApoyoRes}.</p>
 */
export interface IHistoricoApoyo {
  numeroApoyo?: string;
  cvu?: string;
  fechaInicio?: string;
  fechaTermino?: string;
  nombreConvocatoria?: string;
  tipo?: string;
  grado?: string;
  ubicacion?: string;
  estatus?: string;
  fechaUltimoMovimiento?: string;
  justificacion?: string;
  origen?: string;
}

/**
 * Clase que implementa {@link IHistoricoApoyo}.
 *
 * <p>Permite inicializar un objeto de histórico de apoyo mediante
 * un constructor que acepta un objeto parcial de la misma clase.</p>
 */
export class HistoricoApoyo implements IHistoricoApoyo {
  public numeroApoyo?: string | null;
  public cvu?: string;
  public fechaInicio?: string;
  public fechaTermino?: string;
  public nombreConvocatoria?: string;
  public tipo?: string;
  public grado?: string;
  public ubicacion?: string;
  public estatus?: string;
  public fechaUltimoMovimiento?: string;
  public justificacion?: string;
  public origen?: string;
  constructor(init?: Partial<HistoricoApoyo>) {
    Object.assign(this, init);
  }
}

/**
 * Función que mapea un objeto de la API ({@link IHistoricoApoyoRes}) a
 * un modelo de frontend {@link HistoricoApoyo}.
 *
 * @param api Objeto crudo de la API a mapear.
 * @returns {HistoricoApoyo} Objeto mapeado listo para usar en la UI.
 *
 * @example
 * ```ts
 * const apiResponse: IHistoricoApoyoRes = await service.findByCvu('1234567');
 * const model = mapToHistoricoApoyoModel(apiResponse);
 * ```
 */
export function mapToHistoricoApoyoModel(api: any): HistoricoApoyo {
  return {
    numeroApoyo: api.numeroBeca,
    cvu: api.numeroCVU,
    fechaInicio: api.fechaInicioBeca,
    fechaTermino: api.fechaFinBeca,
    nombreConvocatoria: api.nombreConvocatoria,
    tipo: api.tipoConvocatoria,
    grado: api.descApoyoObtener,
    ubicacion: api.descPais,
    estatus: api.descEstatusBeca,
    fechaUltimoMovimiento: api.fechaUltimoMovimiento,
    justificacion: api.justificacion,
    origen: api.fuente,
  };
}
